import { createSelector } from "reselect";
import compareVersions from "compare-versions";

const getAcmgAnnotation = (state) =>
  state.variantResult && state.variantResult.acmg_annotation;

const getVariantId = (state) =>
  state.variantResult && state.variantResult.variant_id;

const getAdditionalSources = (state) =>
  state.variantResult && state.variantResult.additional_sources;

const getAcmgTranscriptName = createSelector(
  getAcmgAnnotation,
  (acmgAnnotation) => acmgAnnotation && acmgAnnotation.transcript
);

const getAcmgTranscriptReason = createSelector(
  getAcmgAnnotation,
  (acmgAnnotation) => acmgAnnotation && acmgAnnotation.transcript_reason
);

const getAcmgCodingImpact = createSelector(
  getAcmgAnnotation,
  (acmgAnnotation) => acmgAnnotation && acmgAnnotation.coding_impact
);

const getAcmgGene = createSelector(
  getAcmgAnnotation,
  (acmgAnnotation) => acmgAnnotation && acmgAnnotation.gene_symbol
);

const getAcmgCancerExplain = createSelector(
  getAcmgAnnotation,
  (acmgAnnotation) => acmgAnnotation && acmgAnnotation.is_cancer_explain
);

const isThaliaVersionSmallerThanNinePointOne = createSelector(
  getAcmgAnnotation,
  (acmgAnnotation) => {
    const version = acmgAnnotation && acmgAnnotation.version_name;
    return !version ? false : compareVersions.compare(version, "9.1.0", "<");
  }
);

const isThaliaVersionSmallerThanElevenPointFour = createSelector(
  getAcmgAnnotation,
  (acmgAnnotation) => {
    const version = acmgAnnotation && acmgAnnotation.version_name;
    return !version ? false : compareVersions.compare(version, "11.4", "<");
  }
);

const isThaliaVersionSmallerThanElevenPointSeven = createSelector(
  getAcmgAnnotation,
  (acmgAnnotation) => {
    const version = acmgAnnotation && acmgAnnotation.version_name;
    return !version ? false : compareVersions.compare(version, "11.7", "<");
  }
);

const getClassifications = createSelector(
  getAcmgAnnotation,
  (acmgAnnotation) => acmgAnnotation && acmgAnnotation.classifications
);

const getRulesThatMetCriteria = (state) =>
  state.acmgRules && state.acmgRules.filter((c) => c.met_criteria);

const getActiveRulesList = (state) =>
  state.acmgRules
    ? state.acmgRules.map((c) => ({ name: c.name, isActive: !!c.met_criteria }))
    : [];

const getAcmgVerdict = createSelector(
  getAcmgAnnotation,
  (acmgAnnotation) => acmgAnnotation?.verdict?.ACMG_rules?.verdict
);

const getAcmgDefaultVerdictRules = createSelector(
  getAcmgAnnotation,
  (acmgAnnotation) => acmgAnnotation?.verdict?.ACMG_rules
);

const getClinicalScore = createSelector(
  getAcmgAnnotation,
  (acmgAnnotation) => acmgAnnotation?.verdict?.ACMG_rules?.clinical_score
);

const getAdditionalDatabases = createSelector(
  getAdditionalSources,
  (additionalDatabases) => additionalDatabases
);

const getPillsToggleState = (state) => state.acmg.pillsToggleState;

const getActiveOverrideStrengths = (state) =>
  state.acmg.activeOverrideStrengths;

const getAllRulesToggleState = (state) => {
  if (state.acmgRules) {
    const activeRulesObj = {};
    const activeRulesList = state.acmgRules.map((c) => ({
      name: c.name,
      isActive: !!c.met_criteria,
    }));

    activeRulesList.forEach((rule) => {
      activeRulesObj[rule.name] = rule.isActive;
    });
    const allRules = {
      ...activeRulesObj,
      ...(state.acmg && state.acmg.pillsToggleState
        ? state.acmg.pillsToggleState
        : {}),
    };
    return allRules;
  }

  return {};
};

const getAcmgSampleFindings = createSelector(
  getAcmgAnnotation,
  (variantResults) => {
    return variantResults?.sample_findings;
  }
);

const getAcmgVerdictClassifications = (state) =>
  state?.variantResult?.acmg_annotation?.verdict?.classifications;

const getLastUsedVariantId = (state) => state?.lastUsedVariantId;

const getSelectedCard = (state) => state?.cards?.selectedCard;

const getClinicalEvidenceState = (state) => state?.clinicalEvidenceState;

const getACMGThrottling = (state) =>
  state?.variantResult?.acmg_annotation?.throttling_info;

const getACMGThrottlingStatus = (state) => {
  return !!state?.variantResult?.acmg_annotation?.throttling_info;
};

const getACMGVersionName = (state) =>
  state?.variantResult?.acmg_annotation?.version_name;

export {
  getAcmgAnnotation,
  getVariantId,
  getAcmgTranscriptName,
  getAcmgCodingImpact,
  getAcmgGene,
  getAcmgCancerExplain,
  isThaliaVersionSmallerThanNinePointOne,
  getClassifications,
  getRulesThatMetCriteria,
  getActiveRulesList,
  getAcmgVerdict,
  getClinicalScore,
  getAdditionalDatabases,
  getPillsToggleState,
  getActiveOverrideStrengths,
  getAllRulesToggleState,
  getAcmgSampleFindings,
  getLastUsedVariantId,
  getSelectedCard,
  getClinicalEvidenceState,
  getAcmgVerdictClassifications,
  getACMGThrottling,
  getACMGThrottlingStatus,
  getAcmgDefaultVerdictRules,
  isThaliaVersionSmallerThanElevenPointFour,
  isThaliaVersionSmallerThanElevenPointSeven,
  getACMGVersionName,
  getAcmgTranscriptReason,
};
